<template>
	<div class="flex flex-col gap-2 bg-white shadow sm:rounded-lg p-4 m-2">
		<div class="flex items-center justify-between w-full gap-2">
			<PageLabel :helpPageName="'Assessments'">Assessments</PageLabel>
			<div class="flex flex-row items-center gap-2">
				<Button id="btnNewUser" @click="newAssessment()" v-if="SessionInfo.hasRightsToScreen('Assessments', PageAccess.Create)">
					<FontAwesomeIcon :icon="['far', 'file-plus']" />New Assessment
				</Button>
				<SplitButton
					:model="copyOptions"
					@click="openCopyAssessmentDialog(false)"
					v-if="SessionInfo.hasRightsToScreen('Assessments', PageAccess.Create)"
				>
					<template #default>
						<div class="flex gap-2 items-center">
							<FontAwesomeIcon :icon="['far', 'copy']" class="mr-2" />
							<span>Copy</span>
						</div>
					</template>
				</SplitButton>
			</div>
		</div>
		<DataTable
			:value="assessmentsList"
			responsiveLayout="scroll"
			:row-hover="true"
			:paginator="true"
			:rows="tablePaginator == null ? 10 : tablePaginator.rows"
			:first="tablePaginator == null ? 0 : tablePaginator.first"
			:page="tablePaginator == null ? 0 : tablePaginator.page"
			@page="pageChanged($event)"
			paginatorTemplate="CurrentPageReport FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink RowsPerPageDropdown"
			:rowsPerPageOptions="[10, 20, 50]"
			currentPageReportTemplate="Showing {first} to {last} of {totalRecords}"
			dataKey="id"
			v-model:filters="usersFilters"
			:loading="loading1"
			id="dtAssessmentList"
		>
			<template #header>
				<div class="p-d-flex p-jc-between">
					<Button icon="pi pi-filter-slash" label="Clear" @click="usersFilters['global'].value = null" />
					<span class="relative">
						<i class="pi pi-search absolute inset-y-3 right-3 flex items-center pointer-events-none"></i>
						<InputText class="pl-10" v-model="usersFilters['global'].value" placeholder="Keyword Search" id="UserKeywordSearch" />
					</span>
				</div>
			</template>
			<template #empty> No assessment found. </template>
			<template #loading> Loading assessment data. Please wait. </template>
			<Column field="assessmentName" header="Assessment Name" :sortable="true" style="flex: 0 0 30%">
				<template #body="{ data }">
					<div class="w-full flex items-center cursor-pointer" @click="selectAssessment(data)">
						{{ data.assessmentName }}
						<span v-if="data.isLocked" class="ml-2">
							<FontAwesomeIcon :icon="['fas', 'lock-keyhole']" title="Locked" class="text-yellow-400" v-tooltip="lock.tooltip" />
						</span>
					</div>
				</template>
				<template #filter="{ filterModel }">
					<InputText type="text" v-model="filterModel.value" class="p-column-filter" placeholder="Search by name" />
				</template>
			</Column>
			<Column field="assessmentNumber" header="Assessment Number" :sortable="true" style="flex: 0 0 30%; text-align: center">
				<template #body="{ data }">
					<div class="w-full" @click="selectAssessment(data)">{{ data.asessementIncrementalNumber }}</div>
				</template>
				<template #filter="{ filterModel }">
					<InputText type="text" v-model="filterModel.value" class="p-column-filter" placeholder="Search by Number" />
				</template>
			</Column>
			<Column field="developmentStage.valueTxt" header="Development Stage" :sortable="true" style="flex: 0 0 30%">
				<template #body="{ data }">
					<div class="w-full flex items-center gap-2" @click="selectAssessment(data)">
						{{ data.developmentStage.valueTxt }}
						<i
							v-if="data.developmentStage && data.developmentStage.isActive === false"
							class="pi pi-exclamation-triangle text-yellow-500"
							v-tooltip="'This development stage is no longer active and cannot be selected in new assessments'"
						></i>
					</div>
				</template>
			</Column>
			<Column field="electronicLabNumber" header="Electronic Lab Number" :sortable="true" style="flex: 0 0 30%">
				<template #body="{ data }">
					<div class="w-full" @click="selectAssessment(data)">{{ data.electronicLabNumber }}</div>
				</template>
				<template #filter="{ filterModel }">
					<InputText type="text" v-model="filterModel.value" class="p-column-filter" placeholder="Search by Electronic Lab No" />
				</template>
			</Column>
			<Column field="collaboratorsListTable" header="Assessment Collaborators" style="flex: 0 0 45%">
				<template #body="{ data }">
					<div class="w-full">
						{{ data.collaboratorsListTable }}
					</div>
				</template>
				<template #filter="{ filterModel }">
					<InputText type="text" v-model="filterModel.value" class="p-column-filter" placeholder="Search by Collaborators" />
				</template>
			</Column>
			<Column field="comments" header="Comments" :sortable="true" style="flex: 0 0 5%">
				<template #body="{ data }">
					<div class="w-full">{{ data.comments }}</div>
				</template>
				<template #filter="{ filterModel }">
					<InputText type="text" v-model="filterModel.value" class="p-column-filter" placeholder="Search by Comment Contents" />
				</template>
			</Column>
			<!-- <Column field="project.createdTimeStamp" filterField="createdTimeStamp" header="Project Creation Date" :sortable="true" style="flex: 0 0 45%">
					<template #body="{ data }">
						<div class="w-full" @click="selectAssessment(data)">

							{{
								data.createdTimeStamp
									? new Date(data.createdTimeStamp).toLocaleDateString("en-Uk", { year: "numeric", month: "long", day: "numeric" })
									: ""
							}}
						</div>
					</template>
				</Column> -->
			<Column header="Created" sortable style="width: 10%">
				<template #body="{ data }">
					<I2TimestampDisplay :date="data.createdTimeStamp" :user="data.createdByPerson" />
				</template>
			</Column>
			<Column field="project.updatedTimeStamp" filterField="updatedTimeStamp" header="Last Modified" :sortable="true" style="flex: 0 0 45%">
				<template #body="{ data }">
					<div class="w-full" @click="selectAssessment(data)">
						{{
							data.updatedTimeStamp
								? useDateTimeHelper(data.updatedTimeStamp).toLocalTimeZoneStandardFormat()
								: useDateTimeHelper(data.createdTimeStamp).toLocalTimeZoneStandardFormat()
						}}
					</div>
				</template>
			</Column>
			<Column field="id" header="Actions" style="width: 11%" styleClass="tdLessPadding">
				<template #body="slotProps"
					><span style="cursor: pointer"
						><FontAwesomeIcon
							:icon="['far', 'pen']"
							title="Modify"
							@click="editAssessment(slotProps.data)"
							v-show="SessionInfo.hasRightsToScreen('Assessments', PageAccess.Update)" /></span
					><span style="cursor: pointer"
						><FontAwesomeIcon
							:icon="['far', 'info-circle']"
							title="View"
							@click="editAssessment(slotProps.data)"
							class="ml-2"
							v-show="!SessionInfo.hasRightsToScreen('Assessments', PageAccess.Update)"
					/></span>
					<!-- <span style="cursor: pointer" v-if="slotProps.data.isBookmarked">
							<FontAwesomeIcon :icon="['far', 'bookmark-slash']" title="Bookmarked" @click="bookmarkAssessment(slotProps.data, false)" class="ml-2" />
						</span>
						<span style="cursor: pointer" v-if="!slotProps.data.isBookmarked">
							<FontAwesomeIcon :icon="['far', 'bookmark']" title="Bookmark" @click="bookmarkAssessment(slotProps.data, true)" class="ml-2" />
						</span> -->
					<span style="cursor: pointer">
						<FontAwesomeIcon
							:icon="['far', slotProps.data.favorites.length > 0 ? 'bookmark' : 'bookmark-slash']"
							title="Bookmark"
							@click="addRemoveFavorite(slotProps.data.id)"
							class="ml-2"
						/>
					</span>
					<span style="cursor: pointer"
						><FontAwesomeIcon
							:icon="['far', 'trash']"
							title="Delete"
							@click="deleteAssessment(slotProps.data)"
							class="ml-2"
							v-show="SessionInfo.hasRightsToScreen('Assessments', PageAccess.Delete)"
					/></span>
					<!-- <span style="cursor: pointer"
							><FontAwesomeIcon
								:icon="['far', 'chart-line']"
								title="Result"
								@click="resultAssessment(slotProps.data)"
								class="ml-2"
								v-show="SessionInfo.hasRightsToScreen('Assessments', PageAccess.Read)"
						/></span>  -->
				</template></Column
			>
		</DataTable>

		<div v-if="showDialog">
			<assessmentEdit :assessment="assessment" @cancel="cancel()" @save="save($event)" />
		</div>

		<Dialog v-model:visible="showDialogDelete" :modal="true" style="width: 35vw" :closable="false" contentStyle="overflow:visible">
			<template #header><PageLabel :helpPageName="'DeleteAssessments'">Delete Assessment</PageLabel> </template>
			<FormCard>
				<I2FormRow class="mt-6">
					<h1>Are you sure you would like to delete selected Assessment?</h1>
				</I2FormRow>
			</FormCard>
			<template #footer>
				<I2FormRow right>
					<Button class="btn btn-inverted" @click="showDialogDelete = false" right :disabled="spinnerRuns">Cancel</Button>
					<!-- <Button label="Delete" class="ml-4" @click="deleteAssessmentOK()" right /> -->
					<Button
						class="ml-4 btn btn-primary"
						@click="deleteAssessmentOK()"
						:disabled="spinnerRuns"
						v-show="SessionInfo.hasRightsToScreen('Assessments', PageAccess.Create)"
					>
						<template #default>
							<span v-if="spinnerRuns">
								<FontAwesomeIcon :icon="['fa', 'spinner']" spin class="mr-2" />
								Delete...
							</span>
							<span v-else> Delete </span>
						</template>
					</Button>
				</I2FormRow>
			</template>
		</Dialog>
	</div>
	<div v-if="showCopyAssessmentDialog">
		<copyAssessmentDialog
			:showDialog="true"
			:actualSubProjectId="route.query.subProjectId!.toString()"
			:spinnerRuns="copyAssessmentSpinner"
			:isToAssessment="isToAssessment"
			@cancel="hideCopyAssessmentDialog()"
			@save="saveCopiedAssessment"
		/>
	</div>
</template>
<script setup lang="ts">
import { ref, onMounted, reactive, Prop } from "vue"
import Dialog from "primevue/dialog"
import { ApiClient, Assessment, AssessmentCollaboratorPut, AssessmentCollaborators, Favorites, History } from "../../entities/NSwagGenerated"
import { EventBus } from "../eventBus"
import SplitButton from "primevue/splitbutton"
import { FilterMatchMode, FilterOperator } from "@primevue/core/api"
import Button from "primevue/button"
import InputText from "primevue/inputtext"
import { SessionInfo, PageAccess } from "../auth/personSessionData"
import PageLabel from "./pageLabel.vue"
import DataTable, { DataTablePageEvent } from "primevue/datatable"
import Column from "primevue/column"
import { library } from "@fortawesome/fontawesome-svg-core"
import { faInfoCircle, faTrash, faPen, faChartLine, faBookmark, faBookmarkSlash, faFilePlus, faCopy } from "@fortawesome/pro-regular-svg-icons"
import { faSpinner } from "@fortawesome/free-solid-svg-icons"
import { faLockKeyhole } from "@fortawesome/pro-solid-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/vue-fontawesome"
library.add(faTrash, faPen, faInfoCircle, faChartLine, faBookmark, faBookmarkSlash, faFilePlus, faCopy, faSpinner, faLockKeyhole)
import assessmentEdit from "./assessmentEdit.vue"
import copyAssessmentDialog from "./copyAssessmentDialog.vue"
import { useRouter, useRoute } from "vue-router"
import { useErrorToaster } from "@/composables/toaster"
import { useDateTimeHelper } from "@/composables/dateTimeHelper"
import I2TimestampDisplay from "./I2TimestampDisplay.vue"
import { useAssessmentStore } from "@/store/assessmentStore"
import { useAssessmentLock } from "@/composables/assessmentLock"

const assessmentStore = useAssessmentStore()
const lock = useAssessmentLock(assessmentStore)
const apiClient = new ApiClient(import.meta.env.VITE_API_URL?.toString())
const assessmentsList = ref()
let showDialog = ref(false)
let showDialogDelete = ref(false)
let tablePaginator: any | null = null
const selectedAssessmentId = ref()
let loading1: any = null
const router = useRouter()
const route = useRoute()
let assessment = new Assessment()

const isToAssessment = ref<boolean>(false)
const copyAssessmentSpinner = ref(false)
const spinnerRuns = ref(false)
const showCopyAssessmentDialog = ref(false)
const props = defineProps({
	assessmentId: {
		type: String,
		required: true,
		default: "",
	},
	screenId: {
		type: String,
		required: true,
		default: "",
	},
	generalInformationId: {
		type: String,
		required: true,
		default: "",
	},
	tabName: {
		type: String,
		required: true,
		default: "",
	},
	selectedCompOptions: {
		type: Array,
		required: false,
		default: function () {
			return []
		},
	},
})

let usersFilters = reactive({
	global: { value: null as string | null | undefined, matchMode: FilterMatchMode.CONTAINS },
	assessmentName: { operator: FilterOperator.OR, constraints: [{ value: null, matchMode: FilterMatchMode.STARTS_WITH }] },
	assessmentNumber: { operator: FilterOperator.OR, constraints: [{ value: null, matchMode: FilterMatchMode.STARTS_WITH }] },
	developmentStage: { operator: FilterOperator.OR, constraints: [{ value: null, matchMode: FilterMatchMode.STARTS_WITH }] },
	electronicLabNumber: { operator: FilterOperator.OR, constraints: [{ value: null, matchMode: FilterMatchMode.STARTS_WITH }] },
	assessmentCollaborators: { operator: FilterOperator.OR, constraints: [{ value: null, matchMode: FilterMatchMode.STARTS_WITH }] },
	collaboratorsListTable: { operator: FilterOperator.OR, constraints: [{ value: null, matchMode: FilterMatchMode.STARTS_WITH }] },
	comments: { operator: FilterOperator.OR, constraints: [{ value: null, matchMode: FilterMatchMode.STARTS_WITH }] },
})

onMounted(() => {
	if (localStorage.getItem("assessmentListFilter")) {
		usersFilters = JSON.parse(localStorage.getItem("assessmentListFilter")!)
	}

	EventBus.emit("buildBreadcrumb", {
		type: "assessment",
		label: "",
		id: route.query.subProjectId,
	})
	refresh()
})
const copyOptions = ref([
	{
		label: "To New Assessment",
		icon: "pi pi-file-plus",
		command: () => {
			openCopyAssessmentDialog(false)
		},
	},
	{
		label: "To Existing Assessment",
		icon: "pi pi-file-import",
		command: () => {
			openCopyAssessmentDialog(true)
		},
	},
])

const openCopyAssessmentDialog = (toExistingAssessment: boolean) => {
	isToAssessment.value = toExistingAssessment
	showCopyAssessmentDialog.value = true
}
const hideCopyAssessmentDialog = () => {
	showCopyAssessmentDialog.value = false
}

const saveCopiedAssessment = async (
	selectedAssessmentId: string,
	actualSubProjectId: string,
	newAssessmentName: string | undefined,
	selectedDevelopmentStage: string | undefined,
	isCopyToAssessment: boolean,
	destinationAssessmentId: string | undefined,
	validScreenIds: string[],
	moveToAssessment: boolean
) => {
	try {
		copyAssessmentSpinner.value = true
		const res = await apiClient
			.copyAssessmentToSubProject(
				selectedAssessmentId,
				actualSubProjectId,
				newAssessmentName,
				selectedDevelopmentStage,
				isCopyToAssessment,
				destinationAssessmentId,
				validScreenIds
			)
			.then(
				(res) => {
					copyAssessmentSpinner.value = false
					showCopyAssessmentDialog.value = false
					if (!isCopyToAssessment) usersFilters["global"].value = newAssessmentName ?? null
					if (moveToAssessment) router.push({ name: "assessment", query: { assessmentId: destinationAssessmentId } })
					else refresh()
				},
				(err) => {
					useErrorToaster(err)
					copyAssessmentSpinner.value = false
					// Keep the dialog open on error so the user can retry or cancel manually
				}
			)
	} catch (err) {
		throw new Error(`Network response was not ok: ${err}`)
	}
}
const addRemoveFavorite = (assessmentId: string) => {
	apiClient
		.addRemoveAssessmentToFavoritesPerPersonId(
			Object.assign(new Favorites(), { assessmentId: assessmentId, personId: SessionInfo.currentUser().id, id: self.crypto.randomUUID() })
		)
		.then((res) => {
			refresh()
		})
}

function resultAssessment(assessment: Assessment) {}

function save(assessmentNumber?: string) {
	showDialog.value = false
	// Set the assessment number in the search filter to automatically find the newly created assessment
	if (assessmentNumber) {
		usersFilters["global"].value = assessmentNumber
	}
	refresh()
}
function cancel() {
	selectedAssessmentId.value = null
	showDialog.value = false
	refresh()
}
function newAssessment() {
	selectedAssessmentId.value = null
	assessment = new Assessment()
	assessment.subProjectId = route.query.subProjectId?.toString() //route.query.subProjectId
	showDialog.value = true
}
function deleteAssessment(data: Assessment) {
	selectedAssessmentId.value = data.id
	showDialogDelete.value = true
}
function deleteAssessmentOK() {
	spinnerRuns.value = true
	apiClient.assessmentDelete(selectedAssessmentId.value).then(
		(result) => {
			refresh()
			spinnerRuns.value = false
			showDialogDelete.value = false
		},
		(err) => {
			throw err
		}
	)
}

const logSelectedAssessment = (assessmentId: string | undefined) => {
	if (assessmentId === undefined) return
	apiClient
		.logSelectedAssessmentPerPersonId(
			Object.assign(new History(), { assessmentId: assessmentId, personId: SessionInfo.currentUser().id, id: self.crypto.randomUUID() })
		)
		.then((res) => {})
}

function selectAssessment(data: Assessment) {
	selectedAssessmentId.value = data.id
	logSelectedAssessment(data.id)
	EventBus.emit("restSelectedSubTab")
	router.push({ name: "assessment", query: { assessmentId: data.id } })
}
function editAssessment(data: any) {
	assessment = data
	showDialog.value = true
}

function bookmarkAssessment(data: any, value: boolean) {
	assessment = data
	assessment.isBookmarked = value

	apiClient.postassessmentonly(assessment).then(
		(result) => {
			refresh()
		},
		(err) => {
			throw err
		}
	)

	// let assessmentAndCollaborators = new AssessmentCollaboratorPut()
	// assessmentAndCollaborators.collaborators = selectedPersons.value
	// assessmentAndCollaborators.assessment = assessment
	// assessmentAndCollaborators.assessment.developmentStageId = selectedDevelopmentStage.value.id
	// assessmentAndCollaborators.assessment.productNameId = selectedProductName.value.id
	// apiClient.assessmentPost(assessmentAndCollaborators).then((res) => {
	// 	emit("save")
	// })
}

function collaboratorsList(list: any) {
	let collaboratorsList = new Array<string>()
	list.forEach((el: AssessmentCollaborators) => {
		if (el.person) collaboratorsList.push(el.person.firstName + " " + el.person.lastName)
	})
	return collaboratorsList.join(",")
}

function pageChanged(ev: DataTablePageEvent) {
	tablePaginator = ev
	localStorage.setItem("projectListPaginate", JSON.stringify(ev))
}

function applyFilter(ev: Event) {
	localStorage.setItem("assessmentListFilter", JSON.stringify(usersFilters))
}
function refresh() {
	var subProjectId = route.query.subProjectId?.toString()
	apiClient!.assessmentPerSubProject(subProjectId).then(
		(result) => {
			assessmentsList.value = result
			assessmentsList.value.forEach((el: any) => {
				if (el.isBookmarked == null) {
					el.isBookmarked = false
				}
				el.devStageTxt = el.developmentStage.valueTxt
				el.collaboratorsListTable = el.assessmentCollaborators.length > 0 ? collaboratorsList(Object.values(el.assessmentCollaborators)) : ""
			})
		},
		(err) => {
			throw err
		}
	)
}
</script>
<style lang="css" scoped></style>
